import React, { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { ImageIcon, CheckCircle, XCircle, Clock, FileText, UserCheck, MessageSquare, Search } from "lucide-react";
import { toast } from "react-toastify";
import SummaryApi from "../../common/SummaryApi";
import api from "../../common/apiClient";
import SelectDropdown from "../../customStyles/SelectDropdown";

const AllRegularizationList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // States for frontend searching and status filtering
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // States for handling the Confirmation Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);
  const [actionType, setActionType] = useState(""); // "approve" or "reject"
  const [modalNote, setModalNote] = useState("");
  
  // State for tracking action API submission (Approve/Reject)
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch all regularization requests for Admin
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const apiConfig = SummaryApi.viewAllRegularization;
      
      // Appending '?all=true' to fetch all records instead of only the admin's personal records
      const url = `${apiConfig.url}?all=true`;
      
      const res = await api({ url, method: apiConfig.method });
      if (res.data.success) {
        setData(res.data.data);
      }
    } catch (err) { 
      toast.error("Fetch failed"); 
    } finally { 
      setLoading(false); 
    }
  }, []);

  useEffect(() => { 
    fetchData(); 
  }, [fetchData]);

  // Open confirmation modal
  const openModal = (req, type) => {
    setSelectedReq(req);
    setActionType(type);
    setModalNote(""); // Clear input when opening
    setIsModalOpen(true);
  };

  // Close confirmation modal
  const closeModal = () => {
    if (actionLoading) return; // Prevent closing the modal while processing
    setIsModalOpen(false);
    setSelectedReq(null);
    setActionType("");
    setModalNote("");
  };

  // Submit Approve or Reject action from modal
  const handleConfirmAction = async () => {
    if (!selectedReq) return;
    try {
      setActionLoading(true); // Start loading state for the action
      const apiConfig = actionType === 'approve' ? SummaryApi.approveRegularization : SummaryApi.rejectRegularization;
      
      const res = await api({ 
        url: apiConfig.url(selectedReq._id), 
        method: apiConfig.method, 
        data: { approverNote: modalNote } 
      });

      if (res.data.success) {
        toast.success(`Request ${actionType}d successfully!`);
        closeModal();
        fetchData();
      }
    } catch (err) { 
      toast.error("Action failed"); 
    } finally {
      setActionLoading(false); // Stop loading state for the action
    }
  };

  // Frontend filter logic
  const filteredData = data.filter((item) => {
    // 1. Status Filter matching
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;

    // 2. Search Query matching (Employee Name or User ID)
    const firstName = item.userId?.firstName || "";
    const lastName = item.userId?.lastName || "";
    const fullName = `${firstName} ${lastName}`.toLowerCase();
    const empId = (item.userId?.userId || "").toLowerCase();
    const cleanQuery = searchQuery.trim().toLowerCase();

    const matchesSearch = 
      fullName.includes(cleanQuery) || 
      empId.includes(cleanQuery);

    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-4">
        <div className="w-10 h-10 border-4 border-red-100 border-t-red-600 rounded-full animate-spin"></div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Loading Requests...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      
      {/* Search and Filter Row */}
      <div className="flex flex-col md:flex-row gap-3 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
        
        {/* Search input container */}
        <div className="flex-1 relative flex items-center">
          <Search size={16} className="absolute left-3 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name or Employee ID..."
            className="w-full text-xs pl-10 pr-3 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all text-gray-700"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Status Dropdown container */}
        <div className="w-full md:w-48">
          <SelectDropdown
            options={[
              { id: "all", label: "All Status" },
              { id: "pending", label: "Pending" },
              { id: "approved", label: "Approved" },
              { id: "rejected", label: "Rejected" },
            ]}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            labelKey="label"
            valueKey="id"
            placeholder="All Status"
            ChildClassName="!h-11 !rounded-xl !bg-gray-50 !border-gray-200"
            inputClassName="text-xs text-gray-600"
          />
        </div>

      </div>

      {/* Render Filtered Data List */}
      {filteredData.length > 0 ? (
        filteredData.map((req) => (
          <div key={req._id} className="bg-white p-5 md:p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
            
            {/* Top Row: User details & Status */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <img 
                  src={req.userId?.profilePicture || "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"} 
                  className="w-12 h-12 rounded-full border border-gray-200 object-cover shadow-sm" 
                  alt="avatar" 
                />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-bold text-gray-800 text-sm sm:text-base">
                      {req.userId?.firstName} {req.userId?.lastName}
                    </h4>
                    {req.userId?.userId && (
                      <span className="text-[10px] bg-gray-100 px-2 py-0.5 rounded-md text-gray-500 font-semibold">
                        {req.userId.userId}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-[10px] text-gray-400 font-bold uppercase">
                      {req.date ? format(new Date(req.date), "dd MMM yyyy") : ""}
                    </p>
                    <span className="text-[8px] text-gray-300">•</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border ${
                      req.status === 'pending' 
                        ? 'bg-amber-50 text-amber-600 border-amber-200' 
                        : req.status === 'approved' 
                          ? 'bg-emerald-50 text-emerald-600 border-emerald-200' 
                          : 'bg-red-50 text-red-600 border-red-200'
                    }`}>
                      {req.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Trigger Buttons */}
              {req.status === "pending" && (
                <div className="grid grid-cols-2 sm:flex gap-2 w-full lg:w-auto">
                  <button 
                    onClick={() => openModal(req, "approve")} 
                    className="py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold uppercase flex items-center justify-center gap-1.5 transition-all shadow-sm"
                  >
                    <CheckCircle size={15}/> Approve
                  </button>
                  <button 
                    onClick={() => openModal(req, "reject")} 
                    className="py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold uppercase flex items-center justify-center gap-1.5 transition-all shadow-sm"
                  >
                    <XCircle size={15}/> Reject
                  </button>
                </div>
              )}
            </div>

            {/* Middle Row: Punch Details */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Type</p>
                <p className="text-xs font-bold text-gray-700 capitalize flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                  {req.type?.replace('_', ' ')}
                </p>
              </div>
              
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Requested Time</p>
                <p className="text-xs font-bold text-red-600 flex items-center gap-1.5">
                  <Clock size={12} className="text-red-400" />
                  {req.requestedCheckIn ? format(new Date(req.requestedCheckIn), "hh:mm a") : "N/A"} - {req.requestedCheckOut ? format(new Date(req.requestedCheckOut), "hh:mm a") : "N/A"}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Attachments</p>
                <div className="flex items-center gap-2">
                  {req.attachments?.map((at, i) => (
                    <a 
                      key={at._id || i} 
                      href={at.url} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600 bg-red-50 hover:bg-red-100 px-2.5 py-1 rounded-lg border border-red-100 transition-all"
                    >
                      <ImageIcon size={14} className="text-red-500" /> View #{i+1}
                    </a>
                  ))}
                  {(!req.attachments || req.attachments.length === 0) && (
                    <span className="text-[11px] text-gray-400 font-semibold flex items-center gap-1">
                      <FileText size={12} className="text-gray-300" /> None
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Row: Employee Reason */}
            <div className="mt-4 p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs text-gray-600 leading-relaxed">
              <span className="font-semibold text-gray-400 uppercase text-[9px] block mb-1">Employee Reason</span>
              <span className="italic">"{req.reason}"</span>
            </div>

            {/* Approver Details & Notes */}
            {req.status !== "pending" && req.approverId && (
              <div className={`mt-3 p-3.5 rounded-xl border text-xs leading-relaxed ${
                req.status === "approved" 
                  ? "bg-emerald-50/40 border-emerald-100/70 text-emerald-900" 
                  : "bg-red-50/40 border-red-100/70 text-red-900"
              }`}>
                <div className="flex items-center gap-1.5 font-bold uppercase text-[9px] tracking-wider mb-1.5">
                  {req.status === "approved" ? (
                    <span className="flex items-center gap-1 text-emerald-600">
                      <UserCheck size={12} /> Approved By
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-600">
                      <XCircle size={12} /> Rejected By
                    </span>
                  )}
                </div>
                
                <div className="font-bold text-gray-800 flex flex-wrap items-center gap-1.5">
                  <span>{req.approverId.firstName} {req.approverId.lastName}</span>
                  {req.approvedAt && (
                    <span className="text-[10px] text-gray-400 font-normal">
                      on {format(new Date(req.approvedAt), "dd MMM yyyy, hh:mm a")}
                    </span>
                  )}
                </div>

                {req.approverNote && (
                  <div className="mt-2 pl-3 border-l-2 border-gray-300 dark:border-gray-600 text-gray-600 italic flex items-start gap-1.5">
                    <MessageSquare size={12} className="text-gray-400 mt-0.5 shrink-0" />
                    <span>"{req.approverNote}"</span>
                  </div>
                )}
              </div>
            )}

          </div>
        ))
      ) : (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm text-gray-400 font-bold uppercase text-xs tracking-wider">
          No records found
        </div>
      )}

      {/* Confirmation Modal Overlay */}
      {isModalOpen && selectedReq && (
        <div className="fixed top-[39px] md:top-[49px] inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
            
            {/* Modal Title */}
            <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
              {actionType === "approve" ? (
                <CheckCircle className="text-emerald-600" size={20} />
              ) : (
                <XCircle className="text-red-600" size={20} />
              )}
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                Confirm {actionType}
              </h3>
            </div>
            
            {/* Modal Body */}
            <div className="mt-4 space-y-4">
              <p className="text-xs text-gray-500 leading-relaxed">
                Are you sure you want to <strong className={actionType === "approve" ? "text-emerald-600" : "text-red-600"}>{actionType}</strong> this regularization request for <strong>{selectedReq.userId?.firstName} {selectedReq.userId?.lastName}</strong> on <strong>{selectedReq.date ? format(new Date(selectedReq.date), "dd MMM yyyy") : ""}</strong>?
              </p>
              
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  Remarks / Reason
                </label>
                <textarea
                  placeholder="Type any notes or feedback..."
                  rows={3}
                  disabled={actionLoading}
                  className="text-xs p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 w-full transition-all resize-none disabled:opacity-60"
                  value={modalNote}
                  onChange={(e) => setModalNote(e.target.value)}
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={closeModal}
                disabled={actionLoading}
                className="py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl text-xs font-bold uppercase transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={actionLoading}
                className={`py-2.5 px-4 text-white rounded-xl text-xs font-bold uppercase transition-all flex items-center justify-center gap-2 ${
                  actionType === "approve" 
                    ? "bg-emerald-600 hover:bg-emerald-700 shadow-sm" 
                    : "bg-red-600 hover:bg-red-700 shadow-sm"
                } disabled:opacity-75 disabled:cursor-not-allowed`}
              >
                {actionLoading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default AllRegularizationList;